import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
  Skeleton,
  Card,
  CardContent,
  Button,
  Stack,
  Pagination,
  PaginationItem,
  IconButton,
} from "@mui/material";
import { ChevronLeft, Close } from "@mui/icons-material";
import { NoDataSvg } from "../../../clinicalNotesClinicalRoute/ClinicalNoteSVGs";
import sign from "jwt-encode";

function Intake({ clientDetails }) {
  const [loading, setLoading] = useState(true);
  const [journals, setJournals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [journalId, setJournalId] = useState(null);
  const fetchJournals = async (currentPage) => {
    setLoading(true);
    
    try {
      const response = await fetch(
        "https://ws-stella.unitedwecare.com/api/v1/app/journal_data/list",
        {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNzYzMiJ9.uv527OlLdX13f-ZRxmSBHwjxpK4QwC7Ob2ESYAST8R0`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: clientDetails?.id,
            page: currentPage,
            page_size: 10,
            form_type: "intake"
          }),
        }
      );

      const data = await response.json();
      if (data?.success) {
        setJournals(data?.data?.journal_data);
        setTotalPages(data?.data?.total_pages || 1);
      } else {
        console.error("Failed to fetch journal data.");
      }
    } catch (error) {
      console.error("Error fetching journals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientDetails?.id) return;
    fetchJournals(page);
  }, [page, clientDetails]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  return (
    <div>
      {journalId ? (
        <IntakeViewer
          clientId={clientDetails?.id}
          id={journalId}
          close={() => setJournalId(null)}
        />
      ) : (
        <Box p={4}>
          {loading ? (
            <Stack spacing={2}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={100} />
              ))}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {journals?.length === 0 ? (
                <div className="NoDataAvailable">
                  {NoDataSvg}
                  <p> No Intake Available</p>
                </div>
              ) : (
                journals?.map((journal) => (
                  <Card
                    onClick={() => setJournalId(journal?.id)}
                    key={journal?.id}
                    className=" rounded-4 "
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <CardContent>
                      <Typography variant="body1">{journal?.name}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        {journal?.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Questions: {journal?.questions_count} • Completed:{" "}
                        {journal?.completed ? "Yes" : "No"}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              )}

              {journals?.length > 0 && (
                <Box display="flex" justifyContent="center" pt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    renderItem={(item) => <PaginationItem {...item} />}
                  />
                </Box>
              )}
            </Stack>
          )}
        </Box>
      )}
    </div>
  );
}

function IntakeViewer({ id, clientId, close }) {
  const [loading, setLoading] = useState(true);
  const [intakeData, setIntakeData] = useState([]);
  async function fetchJournalData() {
    const url = `https://ws-stella.unitedwecare.com/api/v1/app/journal_data/read/${clientId}/${id}`;
   
     try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNzYzMiJ9.uv527OlLdX13f-ZRxmSBHwjxpK4QwC7Ob2ESYAST8R0`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data?.success) {
        // Handle successful response
        setIntakeData(data?.data);
      } else {
        // Handle error response
      }
      // console.log(data);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching journal data:", error);
    }
  }

  useEffect(() => {
    if (!id || !clientId) {
      return; // If id is not provided, do not fetch data
    }
    fetchJournalData();
  }, [id, clientId]);

  const getAnswer = (item) => {
    if (item?.reponse_type === "option") {
      const selectedOption = item?.options?.find((opt) => opt?.selected);
      return selectedOption ? selectedOption?.button_text : "";
    } else {
      return item?.user_input || "";
    }
  };

  return (
    <Box>
      <IconButton className="mb-2" onClick={close}>
        <ChevronLeft />
      </IconButton>
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Question</strong>
              </TableCell>
              <TableCell>
                <strong>Answer</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell width={"70%"}>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))
              : intakeData?.questions?.map((item) => (
                  <TableRow key={item?.sequence}>
                    <TableCell width={"70%"}>{item?.text}</TableCell>
                    <TableCell>{getAnswer(item)}</TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Intake;
